// Shared imports
const articleUtils = require('shared/model-helpers/article')
// Client lib imports
const airbrake = require('client/lib/airbrake')
const businessEvents = require('client/lib/business-events')
const { send: ajax, FriendlyRequestError } = require('client/lib/request')
const { openWindow } = require('client/lib/window')
// Client V2 common imports
const clientV1Bridge = require('client-v2/common/client-v1-bridge')
const { navigate } = require('client-v2/common/state/router/actions')
const shareModalActions = require('client-v2/common/containers/share-modal/actions')
const toastsActions = require('client-v2/common/containers/toasts/actions')
const extension = require('client-v2/common/utils/extension')
// Sibling imports
const {
  cacheReportedUrlId,
  getCachedReportedUrlIds
} = require('./utils')

// Action types

const ARTICLE_HAS_MIX_CHANGE_PENDING = 'ARTICLE_HAS_MIX_CHANGE_PENDING'
const ARTICLE_LOADED = 'ARTICLE_LOADED'
const ARTICLE_MIXES_UPDATED = 'ARTICLE_MIXES_UPDATED'
const ARTICLE_DOMAIN_BLOCKED = 'ARTICLE_DOMAIN_BLOCKED'
const ARTICLES_REPORTED = 'ARTICLES_REPORTED'

// Action creators

function articleHasMixChangePending(urlId) {
  return {
    type: ARTICLE_HAS_MIX_CHANGE_PENDING,
    payload: { urlId }
  }
}

function articleMixesUpdated(urlId, mixIds) {
  return {
    type: ARTICLE_MIXES_UPDATED,
    payload: { urlId, mixIds }
  }
}

function articleLoaded({ article, urlId }) {
  return {
    type: ARTICLE_LOADED,
    payload: { article, urlId }
  }
}

// Action dispatchers

function fetchArticle({ article: { hash } }) {
  return function (dispatch) {
    ajax({ url: `/api/v0.2/url/${hash}` })
    .then(result => {
      const article = result.url
      dispatch(articleLoaded({
        article,
        urlId: article['url_id']
      }))
    })
    .catch(err => {
      airbrake.notify(err)
    })
  }
}

function applyReportedArticlesCache() {
  return function (dispatch) {
    const urlIds = getCachedReportedUrlIds()
    if (urlIds.length) {
      dispatch({
        type: ARTICLES_REPORTED,
        payload: { urlIds }
      })
    }
  }
}

function navigateToArticleContent(options) {
  if (options.event && options.event.altKey) {
    return navigateToArticleProfile(options)
  }
  /**
   * We can't use `async`/`await` here because of user-agent pop-up blocking.
   * `window.open` **must** be called as a direct result of user action,
   * but `async`/`await` defers execution, triggering the pop-up blocker.
   */
  return function () {
    const { article, pageContext } = options
    const { url, 'url_id': urlId } = article
    extension.showUrl({
      url,
      'url_id': urlId
    },{
      ...pageContext
    })
    .catch(() => openWindow(url))
    // Send tracking calls
    return Promise.all([
      businessEvents.trackInternal('show-web-content', {
        'actioned_object_type': 'url_id',
        'actioned_object_id': urlId
      }, {
        pageContext
      }),
      businessEvents.trackSegment('show-web-content', {
        hostname: article.hostname,
        url,
        'url_id': urlId
      }, {
        pageContext
      })
    ])
  }
}

function navigateToArticleDomainSearch({ article, event, pageContext }) {
  return navigate({
    event,
    href: articleDomainSearchHref(article),
    pageContext
  })
}

function navigateToArticleProfile({ article, event, pageContext }) {
  return navigate({
    event,
    href: articleProfileHref(article),
    pageContext
  })
}

function showArticleActionsModal(options) {
  // Mitigate circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return function (dispatch) {
    const { article } = options
    clientV1Bridge.showArticleActionsModal(options)
    .then(reason => {
      if (reason === 'block') {
        dispatch({
          type: ARTICLE_DOMAIN_BLOCKED,
          payload: {
            hostname: article.hostname
          }
        })
      } else if (reason) {
        const urlId = article['url_id']
        cacheReportedUrlId(urlId)
        dispatch({
          type: ARTICLES_REPORTED,
          payload: {
            urlIds: [urlId]
          }
        })
      }
    })
    .catch(err => { airbrake.notify(err) })
  }
}

function showArticleProfileModal(options) {
  // Mitigate circular dependencies
  const articleProfileModalActions = require('client-v2/common/containers/article-profile/state/modal-actions')
  // Allow opening article profile in a new tab instead of popping the modal
  if (options.event && options.event.altKey) {
    return navigateToArticleProfile(options)
  }
  return function (dispatch, getState) {
    const { articleProfileModal } = getState()
    if (typeof articleProfileModal === 'undefined') {
      // Fall-back to article profile page if modal is not available
      return dispatch(navigateToArticleProfile(options))
    }
    return dispatch(articleProfileModalActions.show(options))
  }
}

function showArticleShareModal({ article }) {
  return shareModalActions.show({ model: article })
}

function showAddUrlModal() {
  // Work-around circular dependency
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  return async function (dispatch) {
    const url = await clientV1Bridge.showAddUrlModal()
    if (url) {
      return dispatch(showCollectionSelectModal({ url }))
    }
  }
}

function showCollectionSelectModal(options) {
  // Work-around circular dependencies
  const clientV1Bridge = require('client-v2/common/client-v1-bridge')
  const collectionSelectModalActions = require('client-v2/common/containers/collection-select-modal/actions')
  return async function (dispatch, getState) {
    if (getState().user.getExperiment('*lightweight_engagement') === 'disabled') {
      return clientV1Bridge.showMixSelectModal(options)
    }
    return dispatch(collectionSelectModalActions.show(options))
  }
}

function handleArticleAddAction(options) {
  // Work-around circular dependencies
  return async function (dispatch, getState) {
    if (getState().user.getExperiment('*lightweight_engagement') === 'disabled') {
      return dispatch(showCollectionSelectModal(options))
    }
    let { article } = options
    if (article.hasMix === ARTICLE_HAS_MIX_CHANGE_PENDING) {
      return // Ignore actions while pending
    }
    if (!article.hasMix) {
      await dispatch(addToMixedCollection(options))
      article = { ...article, hasMix: true }
    }
    return dispatch(showCollectionSelectModal({ ...options, article }))
  }
}

function addToMixedCollection({ article }) {
  const collectionSelectModalAPI = require('client-v2/common/containers/collection-select-modal/api')
  return async function (dispatch) {
    const urlId = article['url_id']
    dispatch(articleHasMixChangePending(urlId))
    try {
      const result = await collectionSelectModalAPI.addToMixedCollection(urlId)
      dispatch(articleMixesUpdated(urlId, [result.mixId]))
      extension.clearUrlCache(urlId)
      dispatch(toastsActions.showNotice({
        message: 'Mixed to your profile'
      }))
    } catch (err) {
      // Cancel pending state
      dispatch(articleMixesUpdated(urlId, []))
      // Present the error as an alert dialog
      clientV1Bridge.showAlertModal({
        heading: 'Sorry.',
        subheading: (
          err instanceof FriendlyRequestError
          ? err.message
          : 'We could not mix this post. Please try again later.'
        )
      })
      throw err
    }
  }
}

function trackCardView({ article, pageContext }) {
  return async function () {
    await Promise.all([
      businessEvents.trackInternal('view-card', {
        'actioned_object_type': 'url_id',
        'actioned_object_id': article['url_id']
      }, {
        pageContext
      }),
      businessEvents.trackSegment('view-card', {
        'url_id': article['url_id']
      }, {
        pageContext
      })
    ])
  }
}

function trackGridView({ article, pageContext, recPosition }) {
  return async function () {
    await Promise.all([
      businessEvents.trackInternal('view-grid', {
        'actioned_object_type': 'url_id',
        'actioned_object_id': article['url_id'],
        'actioned_object_position': recPosition
      }, {
        pageContext
      }),
      businessEvents.trackSegment('view-grid', {
        'url_id': article['url_id']
      }, {
        pageContext
      })
    ])
  }
}

// Helpers

function articleProfileHref(article) {
  return articleUtils.prettyPath(article.hash, article.title)
}

function articleDomainSearchHref(article) {
  return `/search/${article.hostname}/domain`
}

// Action exports

module.exports = {
  // Types
  ARTICLE_DOMAIN_BLOCKED,
  ARTICLE_HAS_MIX_CHANGE_PENDING,
  ARTICLE_LOADED,
  ARTICLE_MIXES_UPDATED,
  ARTICLES_REPORTED,
  // Creators
  articleHasMixChangePending,
  articleLoaded,
  articleMixesUpdated,
  // Dispatchers
  addToMixedCollection,
  applyReportedArticlesCache,
  fetchArticle,
  handleArticleAddAction,
  navigateToArticleContent,
  navigateToArticleDomainSearch,
  navigateToArticleProfile,
  showArticleActionsModal,
  showArticleProfileModal,
  showArticleShareModal,
  showAddUrlModal,
  showCollectionSelectModal,
  trackCardView,
  trackGridView,
  // Helpers
  articleProfileHref,
  articleDomainSearchHref
}
